import moment from 'moment-timezone';

import { MentionImportTypesEnum } from '../../../Common/MonitorShared/enum/mention-import-types.enum';
import { LicensesEnum, SocialLicenses } from '../../../api/monitoring/licenses.enum';
import {
    MonitoringAccountConfigurationApiModel,
    TvEyesConfiguration,
    GeoRestriction
} from '../../../api/monitoring/model/monitoring-account-configuration-api.model';
import { IncludeExcludeModel } from '../../../Common/Misc/models/include-exclude-model';
import { MentionImportTypes } from '../../../Common/MonitorShared/misc/mention-import-types';
import { sublicenseMap, SublicensesEnum } from '../../../api/monitoring/sublicenses.enum';


export type SubscriptionLicenses = {[key in LicensesEnum]?: boolean};
export type Sublicenses = {[key in SublicensesEnum]?: boolean};

export interface MonitoringConfigTVEyes {
    isArchiveProvisioned: boolean;
    isDownloadProvisioned: boolean;
    geos: IncludeExcludeModel<string>;
}

export class MonitoringConfigsModel {
    licenses: SubscriptionLicenses = {};
    sublicenses: Sublicenses = {};
    socialLicenses: SubscriptionLicenses = {};
    tvEyesOptions: MonitoringConfigTVEyes;
    burrellesAccountIds: string[] = [];
    taggingSearchLimit: number = 25;
    prominenceGroupLimit: number = 0;
    mentionImportTypes: MentionImportTypes = {};
    hasMentionInsights: boolean;
    hasCoverageInsights: boolean;
    unsavedMentionExportLimit: number = 50000;
    autoSaveTopicLimit: number;
    activeAutoSaveTopicTotal: number;
    savingQuotaStart: Date;
    savingQuotaEnd: Date;
    savingQuota?: number;
    additionalMentionQuota?: number;
    savingQuotaUsed?: number;
    savingQuotaRemaining?: number;
    geoRestrictions?: GeoRestriction[] = [];

    constructor(apiModel?: MonitoringAccountConfigurationApiModel) {
        for (const licenseId of Object.values(LicensesEnum)) {
            // "Online (Moreover)" and "CS Supplement (Snapshot and CSV)" is checked by default
            this.licenses[licenseId] = apiModel
                ? false
                : [LicensesEnum.MOREOVER, LicensesEnum.SNAPSHOT, LicensesEnum.CSV].includes(licenseId as LicensesEnum);
        }
        for (const licenseId of SocialLicenses) {
            this.socialLicenses[licenseId] = false;
        }
        for (const sublicenseId of Object.values(SublicensesEnum)) {
            this.sublicenses[sublicenseId] = false;
        }

        this.savingQuota = 25000;
        this.additionalMentionQuota = 0;
        this.savingQuotaUsed = 0;
        this.savingQuotaStart = moment().utc().startOf('month').toDate(); // start of the month
        this.savingQuotaEnd = moment().utc().endOf('month').toDate(); // end of the month

        if (apiModel) {
            this.taggingSearchLimit = apiModel.taggingSearchLimit;

            if (apiModel.licenses) {
                apiModel.licenses.forEach(x => this.licenses[x] = true);
            }

            if (apiModel.socialLicenses) {
                apiModel.socialLicenses.forEach(x => this.socialLicenses[x] = true);
            }

            if (apiModel.sublicenses) {
                apiModel.sublicenses.forEach(x => this.sublicenses[x] = true);
            }

            if (apiModel.geoRestrictions) {
                this.geoRestrictions = apiModel.geoRestrictions;
            }

            if (apiModel.tveyes) {
                this.tvEyesOptions = {
                    geos: new IncludeExcludeModel<string>(),
                    isArchiveProvisioned: apiModel.tveyes.isArchiveProvisioned,
                    isDownloadProvisioned: apiModel.tveyes.isDownloadProvisioned
                };
                this.tvEyesOptions.geos.include = apiModel.tveyes.geoInclude;
                this.tvEyesOptions.geos.exclude = apiModel.tveyes.geoExclude;
            }
            if (apiModel.burrellesAccountIds) {
                this.burrellesAccountIds = apiModel.burrellesAccountIds.slice();
            }

            this.prominenceGroupLimit = apiModel.prominenceGroupLimit;

            const mentionImportTypes = {} as MentionImportTypes;
            Object.values(MentionImportTypesEnum).forEach(x => mentionImportTypes[x] = false);
            apiModel.mentionImportTypes.forEach(x => mentionImportTypes[x] = true);
            this.mentionImportTypes = mentionImportTypes;
            this.hasMentionInsights = apiModel.hasMentionInsights;
            this.hasCoverageInsights = apiModel.hasCoverageInsights;

            this.unsavedMentionExportLimit = apiModel.unsavedMentionExportLimit;
            this.autoSaveTopicLimit = apiModel.autoSaveTopicLimit;
            this.activeAutoSaveTopicTotal = apiModel.activeAutoSaveTopicTotal;

            this.savingQuotaStart = new Date(apiModel.savingQuotaStart);
            this.savingQuotaEnd = new Date(apiModel.savingQuotaEnd);

            if (apiModel.savingQuota === null || apiModel.savingQuota === undefined) {
                // B/E treats null as unlimited whereas -1 is treated as unlimited on F/E
                this.savingQuota = -1;
            } else {
                this.savingQuota = apiModel.savingQuota;
            }

            this.additionalMentionQuota = apiModel.additionalMentionQuota ?? 0;
            this.savingQuotaUsed = apiModel.savingQuotaUsed;
        }

        this.savingQuotaRemaining = this.savingQuota === -1 ? -1 : (this.savingQuota + this.additionalMentionQuota) - this.savingQuotaUsed;
    }

    toApiModel(): MonitoringAccountConfigurationApiModel {
        const apiModel = new MonitoringAccountConfigurationApiModel();

        apiModel.licenses = Object.values(LicensesEnum).filter(licenseId => this.licenses[licenseId]) as number[];
        apiModel.socialLicenses = SocialLicenses.filter(license => this.socialLicenses[license]);
        apiModel.sublicenses = Object.values(SublicensesEnum).filter(sublicenseId => this.sublicenses[sublicenseId]) as number[];
        apiModel.geoRestrictions = this.geoRestrictions;

        if (this.tvEyesOptions && this.licenses[LicensesEnum.TVEYES]) {
            apiModel.tveyes = new TvEyesConfiguration();
            apiModel.tveyes.geoInclude = this.tvEyesOptions.geos.include;
            apiModel.tveyes.geoExclude = this.tvEyesOptions.geos.exclude;
            apiModel.tveyes.isArchiveProvisioned = this.tvEyesOptions.isArchiveProvisioned;
            apiModel.tveyes.isDownloadProvisioned = this.tvEyesOptions.isDownloadProvisioned;
        }

        if (this.licenses[LicensesEnum.BURELLES_LUCE] && this.burrellesAccountIds) {
            apiModel.burrellesAccountIds = this.burrellesAccountIds.filter(x => !!x);
        }

        apiModel.taggingSearchLimit = this.taggingSearchLimit;
        apiModel.prominenceGroupLimit = this.prominenceGroupLimit;
        apiModel.mentionImportTypes = Object.values(MentionImportTypesEnum).filter(x => this.mentionImportTypes[x]);
        apiModel.hasMentionInsights = this.hasMentionInsights;
        apiModel.hasCoverageInsights = this.hasCoverageInsights;
        apiModel.unsavedMentionExportLimit = this.unsavedMentionExportLimit;
        apiModel.autoSaveTopicLimit = this.autoSaveTopicLimit;

        if (this.savingQuota === -1) {
            // B/E treats null as unlimited whereas -1 is treated as unlimited on F/E
            apiModel.savingQuota = null;
        } else {
            apiModel.savingQuota = this.savingQuota;
        }

        apiModel.additionalMentionQuota = this.additionalMentionQuota;

        return apiModel;
    }

    isValid(): boolean {
        // If the burelles license is on, at least one account must be specified
        if (
            this.licenses[LicensesEnum.BURELLES_LUCE] &&
            (!this.burrellesAccountIds || !this.burrellesAccountIds.filter(x => !!x).length)
        ) {
            return false;
        }

        return true;
    }

    /**
     * Removes all non-social monitoring licenses, primary use case is when monitoring gets removed from an account that previously had it
     */
    removeAllMonitoringLicenses() {
        for (const licenseId of Object.values(LicensesEnum)) {
            // "Online (Moreover)" and "CS Supplement (Snapshot and CSV)" will remain checked
            this.licenses[licenseId] = [LicensesEnum.MOREOVER, LicensesEnum.SNAPSHOT, LicensesEnum.CSV].includes(licenseId as LicensesEnum);
        }
    }

    hasSubscriptionLicenses(): boolean {
        const monitoringSubscriptionLicenses = [
            LicensesEnum.FACEBOOK,
            LicensesEnum.INSTAGRAM,
            LicensesEnum.REDDIT,
            LicensesEnum.TWITTER,
            LicensesEnum.YOUTUBE,
            LicensesEnum.TVEYES,
            LicensesEnum.PODCASTS,
            LicensesEnum.FINANCIAL_PODCASTS,
        ];

        return monitoringSubscriptionLicenses.some(license => this.licenses[license]) || this.hasAnySocialLicenses();
    }

    hasAnyLicenses(): boolean {
        return Object.keys(LicensesEnum).some(k => this.licenses[k]);
    }

    hasAnySocialLicenses(): boolean {
        return Object.values(this.socialLicenses).some(Boolean);
    }

    sublicensesAreValid(): boolean {
        const licensesWithSublicenses = Array.from(sublicenseMap.values());
        for (const license of licensesWithSublicenses) {
            if (this.licenses[license]) {
                // The license that has sublicenses is on, at least one of it's sublicenses need to be on
                let foundLicense = false;

                for (const sublicense of Object.values(SublicensesEnum)) {
                    if (this.sublicenses[sublicense] && sublicenseMap.get(sublicense as SublicensesEnum) === license) {
                        foundLicense = true;
                        break;
                    }
                }

                if (!foundLicense) {
                    return false;
                }
            }
        }
        return true;
    }
}
